/**
 * #725 — the trader's live-trading key mapping, stored PER AUTHENTICATED USER.
 *
 * Same lifecycle as `savedGames` / `minimizedGames`, and for the same reason
 * review gave: a browser-wide payload means signing in as a second trader on
 * a shared machine inherits the first trader's keys. For a preference that
 * decides which keystroke places an order, inheriting silently is the wrong
 * failure — the second trader would be told their keys are one thing while
 * the board answers to another.
 *
 * ### Ownership is enforced on READ, not only on hydration
 *
 * `selectOwnedHotkeys` yields the DEFAULTS unless the loaded mapping belongs
 * to the viewer. Hydration is an effect and can lag a user change by a frame;
 * an ownership gate cannot. Falling back to the defaults is also the safe
 * direction: A/D/W/E/Space is what every trader already expects, so a lagging
 * hydration degrades to shipped behaviour rather than to another account's.
 *
 * The persisted mapping is never erased on logout — signing back in restores
 * your own keys, exactly like your saved games.
 */
import { create } from 'zustand';
import { storageKey } from '@/lib/infra/storage';
import {
  sanitizeHotkeyMap,
  type LiveHotkeyActionId,
  type LiveHotkeyMap,
} from '@/lib/trading/liveHotkeyRegistry';

/** Frozen empty map: the shared "no mapping" identity, so a gated read does
 *  not hand subscribers a fresh object every render. */
const NO_HOTKEYS: LiveHotkeyMap = Object.freeze({});

function storageKeyFor(userId: string): string {
  return storageKey(`galactus:liveHotkeys:${userId}`);
}

function load(userId: string): LiveHotkeyMap {
  try {
    const raw = localStorage.getItem(storageKeyFor(userId));
    if (!raw) return {};
    // Sanitize rather than trust: a stored key that is now reserved, or that
    // duplicates one an earlier action explicitly stored, falls back to the
    // default here instead of reaching the listener. It deliberately does NOT
    // judge a stored key against another action's DEFAULT — whether that
    // default is live depends on the trader's grants, which storage cannot
    // see, and doing so used to discard legal bindings on their way to disk.
    return sanitizeHotkeyMap(JSON.parse(raw));
  } catch {
    // Unavailable or malformed localStorage — the shipped defaults.
    return {};
  }
}

function persist(userId: string, map: LiveHotkeyMap): void {
  try {
    localStorage.setItem(storageKeyFor(userId), JSON.stringify(map));
  } catch {
    // Full or unavailable localStorage — the session still works in memory.
  }
}

interface LiveHotkeysState {
  /** Who the loaded mapping belongs to. Null between logout and hydration. */
  userId: string | null;
  hotkeys: LiveHotkeyMap;
  hydrateFor: (userId: string | null) => void;
  setHotkey: (id: LiveHotkeyActionId, key: string) => void;
  reset: () => void;
}

export const useLiveHotkeysStore = create<LiveHotkeysState>((set, get) => ({
  userId: null,
  hotkeys: NO_HOTKEYS,

  hydrateFor: (userId) => {
    if (get().userId === userId) return;
    if (!userId) {
      set({ userId: null, hotkeys: NO_HOTKEYS });
      return;
    }
    set({ userId, hotkeys: load(userId) });
  },

  setHotkey: (id, key) => {
    const { userId, hotkeys } = get();
    // No signed-in owner means nothing to write against. Silent: the panel is
    // unreachable without a session, so this is a guard, not a user path.
    if (!userId) return;
    // Re-sanitize the WHOLE map, so the no-two-actions-share-a-key invariant
    // holds of the stored value itself rather than only of the UI path.
    const next = sanitizeHotkeyMap({ ...hotkeys, [id]: key });
    set({ hotkeys: next });
    persist(userId, next);
  },

  reset: () => {
    const { userId } = get();
    if (!userId) return;
    set({ hotkeys: NO_HOTKEYS });
    persist(userId, {});
  },
}));

/**
 * The mapping to USE. Yields the shipped defaults unless it belongs to the
 * viewer — see the ownership note in the module header.
 */
export function selectOwnedHotkeys(
  state: { userId: string | null; hotkeys: LiveHotkeyMap },
  viewerId: string | null,
): LiveHotkeyMap {
  return viewerId != null && state.userId === viewerId ? state.hotkeys : NO_HOTKEYS;
}

/**
 * Clear the in-memory mapping. The persisted one is deliberately left intact.
 *
 * Called from `authStore.purgeUserCaches`, which runs on EVERY path to
 * unauthenticated — including the cross-tab logout handler. A component effect
 * cannot be the only reset: logout unmounts the pages that hold one.
 */
export function resetLiveHotkeys(): void {
  useLiveHotkeysStore.getState().hydrateFor(null);
}

/**
 * Cross-tab sync, scoped to the CURRENT owner.
 *
 * The old browser-wide store got this for free; keying per user means doing it
 * deliberately. The `storage` event fires only in OTHER tabs, so re-reading
 * here cannot loop.
 *
 * What the key check actually buys — stated precisely, because the obvious
 * claim is wrong: it is NOT what stops two traders overwriting each other.
 * `load()` reads THIS store's own `userId`, so an unrelated event would just
 * re-read the right key harmlessly. What it prevents is a re-render storm:
 * without it, every unrelated localStorage write anywhere in the app produces
 * a fresh `hotkeys` object and re-renders every teaching surface on the
 * trading board.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    const { userId } = useLiveHotkeysStore.getState();
    if (!userId || e.key !== storageKeyFor(userId)) return;
    useLiveHotkeysStore.setState({ hotkeys: load(userId) });
  });
}
